import React from "react";
import { ArrowDown, ArrowUp, Plus, Trash2 } from "react-feather";
import { Button, Col, FormGroup, Input, Label, Row } from "reactstrap";
import MultiSelectField from "../../common/MultiSelectField";
import { Currency } from "../../../constants/currency";

// Slots of a build-your-own box. Each slot lists the products a customer may put in it
// and how many they must pick (min = max for an ordinary "pick one" slot). A product's
// Price Extra is added to the box price per unit picked.
//
// Slot shape: { key, name, arabicName, minSelectionCount, maxSelectionCount,
//               allowDuplicates, items: [{ itemProductId, priceExtra, isDefault }] }

let slotKeySeq = 0;
const newSlotKey = () => `slot-${Date.now()}-${(slotKeySeq += 1)}`;

/** Slots as the product page returns them → editor state. */
export const slotsFromProduct = (selectionGroups) =>
  (selectionGroups || []).map((group) => ({
    key: newSlotKey(),
    name: group.name || "",
    arabicName: group.arabicName || "",
    minSelectionCount: group.minSelectionCount,
    maxSelectionCount: group.maxSelectionCount,
    allowDuplicates: !!group.allowDuplicates,
    items: (group.items || []).map((item) => ({
      itemProductId: item.productId,
      priceExtra: item.priceExtra || 0,
      isDefault: !!item.isDefault,
    })),
  }));

/** Editor state → the replace-set updateSystemProduct takes. Order on screen is the sort order. */
export const slotsToPayload = (slots) =>
  slots.map(({ key, ...slot }, index) => ({
    ...slot,
    sortOrder: index,
    items: slot.items.map((item, itemIndex) => ({
      ...item,
      priceExtra: item.priceExtra === "" ? 0 : item.priceExtra,
      sortOrder: itemIndex,
    })),
  }));

const pickRule = (slot) => {
  const min = parseInt(slot.minSelectionCount);
  const max = parseInt(slot.maxSelectionCount);
  if (Number.isNaN(min) || Number.isNaN(max)) return "";
  if (min === max) return `Customer picks exactly ${min}.`;
  if (min === 0) return `Optional — customer picks up to ${max}.`;
  return `Customer picks ${min} to ${max}.`;
};

const BoxSlotsEditor = ({ slots, onChange, candidates }) => {
  const candidateById = new Map(candidates.map((p) => [p.id, p]));
  const options = candidates.map((p) => ({
    value: p.id,
    label: `${p.title}${p.is_active ? "" : " (inactive)"} — ${p.price} ${Currency.sr}`,
  }));

  const update = (index, patch) =>
    onChange(slots.map((slot, i) => (i === index ? { ...slot, ...patch } : slot)));

  const setItems = (index, selected) => {
    const current = slots[index].items;
    const items = (selected || []).map(
      (option) =>
        current.find((item) => item.itemProductId === option.value) || {
          itemProductId: option.value,
          priceExtra: 0,
          isDefault: false,
        }
    );
    update(index, { items });
  };

  const updateItem = (index, itemProductId, patch) =>
    update(index, {
      items: slots[index].items.map((item) =>
        item.itemProductId === itemProductId ? { ...item, ...patch } : item
      ),
    });

  const move = (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= slots.length) return;
    const next = [...slots];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  const addSlot = () =>
    onChange([
      ...slots,
      {
        key: newSlotKey(),
        name: `Slot ${slots.length + 1}`,
        arabicName: "",
        minSelectionCount: 1,
        maxSelectionCount: 1,
        allowDuplicates: false,
        items: [],
      },
    ]);

  return (
    <div>
      {slots.length === 0 && (
        <div style={{ color: "#6b7280", marginBottom: "12px" }}>
          No slots yet. A box of 5 where every pick is separate needs 5 slots of "pick 1"; a box
          where the customer picks any 5 from one list needs 1 slot of "pick 5".
        </div>
      )}

      {slots.map((slot, index) => (
        <div
          key={slot.key}
          style={{ border: "1px solid #e5e7eb", borderRadius: "10px", padding: "16px", marginBottom: "14px" }}
        >
          <Row className="align-items-end">
            <Col md="3">
              <FormGroup>
                <Label>Slot name</Label>
                <Input
                  type="text"
                  value={slot.name}
                  onChange={(e) => update(index, { name: e.target.value })}
                />
              </FormGroup>
            </Col>
            <Col md="3">
              <FormGroup>
                <Label>Arabic name</Label>
                <Input
                  type="text"
                  dir="rtl"
                  value={slot.arabicName}
                  onChange={(e) => update(index, { arabicName: e.target.value })}
                />
              </FormGroup>
            </Col>
            <Col md="1">
              <FormGroup>
                <Label>Min</Label>
                <Input
                  type="number"
                  min="0"
                  value={slot.minSelectionCount}
                  onChange={(e) => update(index, { minSelectionCount: e.target.value === "" ? "" : parseInt(e.target.value) })}
                />
              </FormGroup>
            </Col>
            <Col md="1">
              <FormGroup>
                <Label>Max</Label>
                <Input
                  type="number"
                  min="1"
                  value={slot.maxSelectionCount}
                  onChange={(e) => update(index, { maxSelectionCount: e.target.value === "" ? "" : parseInt(e.target.value) })}
                />
              </FormGroup>
            </Col>
            <Col md="2">
              <FormGroup check style={{ marginBottom: "1rem" }}>
                <Label check style={{ cursor: "pointer" }}>
                  <Input
                    type="checkbox"
                    checked={slot.allowDuplicates}
                    onChange={(e) => update(index, { allowDuplicates: e.target.checked })}
                    style={{ marginRight: "6px" }}
                  />
                  Allow repeats
                </Label>
              </FormGroup>
            </Col>
            <Col md="2" className="d-flex justify-content-end gap-1" style={{ marginBottom: "1rem" }}>
              <Button size="sm" color="secondary" title="Move up" disabled={index === 0} onClick={() => move(index, -1)}>
                <ArrowUp size={14} />
              </Button>
              <Button size="sm" color="secondary" title="Move down" disabled={index === slots.length - 1} onClick={() => move(index, 1)}>
                <ArrowDown size={14} />
              </Button>
              <Button size="sm" color="danger" title="Remove slot" onClick={() => onChange(slots.filter((_, i) => i !== index))}>
                <Trash2 size={14} />
              </Button>
            </Col>
          </Row>

          <small style={{ color: "#6b7280" }}>{pickRule(slot)}</small>

          <FormGroup style={{ marginTop: "10px" }}>
            <Label>Products in this slot</Label>
            <MultiSelectField
              options={options}
              value={options.filter((o) => slot.items.some((item) => item.itemProductId === o.value))}
              onChange={(selected) => setItems(index, selected)}
              placeholder="Choose the products a customer can pick..."
              menuPortalTarget={document.body}
            />
          </FormGroup>

          {slot.items.map((item) => {
            const product = candidateById.get(item.itemProductId);
            return (
              <Row key={item.itemProductId} className="align-items-center" style={{ marginBottom: "6px", marginLeft: "4px" }}>
                <Col md="5">
                  {product?.title || `Product ${item.itemProductId}`}
                  {product && !product.is_active && (
                    <span style={{ color: "#b45309", marginLeft: "6px" }}>(inactive — hidden from customers)</span>
                  )}
                </Col>
                <Col md="3">
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Price Extra"
                    value={item.priceExtra}
                    onChange={(e) =>
                      updateItem(index, item.itemProductId, {
                        priceExtra: e.target.value === "" ? "" : parseFloat(e.target.value) || 0,
                      })
                    }
                  />
                </Col>
                <Col md="3">
                  <Label check style={{ cursor: "pointer", color: "#6b7280" }}>
                    <Input
                      type="checkbox"
                      checked={item.isDefault}
                      onChange={(e) => updateItem(index, item.itemProductId, { isDefault: e.target.checked })}
                      style={{ marginRight: "6px" }}
                    />
                    Pre-selected
                  </Label>
                </Col>
              </Row>
            );
          })}
        </div>
      ))}

      <Button color="primary" size="sm" onClick={addSlot}>
        <Plus size={14} className="me-1" /> Add slot
      </Button>
    </div>
  );
};

export default BoxSlotsEditor;
